import { useState, type FormEvent } from 'react'
import { useChangeProjectNeeds, useProjectNeeds } from '../api/queries'
import type { ProjectNeed, ProjectNeedStep, UserRef } from '../api/types'
import { formatDateTime } from '../lib/format'
import { Avatar, Card, ErrorBox } from './ui'

/**
 * "Til næste gang" øverst på sagen (beslutning 143).
 *
 * Korte noter om, hvad der mangler, før man kører ud igen: "mangler 3 stk. 16A
 * automatsikring". Skal det bestilles eller tages med, får noten knapperne
 * "Bestilt" og "Pakket i sagskassen", der hver husker hvem og hvornår. Det, der
 * mangler, står øverst; det klarede er foldet sammen nederst.
 *
 * Ikke opgavens mangelliste og ikke afleveringens mangler - se beslutningen.
 */
export function ProjectNeedsCard({ projectId }: { projectId: number }) {
  const { data, isLoading, error } = useProjectNeeds(projectId)
  const change = useChangeProjectNeeds(projectId)
  const [text, setText] = useState('')
  const [needsOrder, setNeedsOrder] = useState(false)
  const [needsPacking, setNeedsPacking] = useState(false)
  const [showDone, setShowDone] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  if (isLoading) return null
  if (error) return <ErrorBox error={error} />

  const needs = data ?? []
  const open = needs.filter((n) => !n.isDone)
  const done = needs.filter((n) => n.isDone)

  async function run(action: () => Promise<unknown>) {
    setActionError(null)
    try {
      await action()
      return true
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Det lykkedes ikke.')
      return false
    }
  }

  async function add(event: FormEvent) {
    event.preventDefault()
    const value = text.trim()
    if (!value) return
    const ok = await run(() => change.mutateAsync({ kind: 'add', text: value, needsOrder, needsPacking }))
    if (ok) {
      setText('')
      setNeedsOrder(false)
      setNeedsPacking(false)
    }
  }

  const setStep = (need: ProjectNeed, step: ProjectNeedStep, value: boolean) =>
    void run(() => change.mutateAsync({ kind: 'step', needId: need.id, step, value }))

  const remove = (need: ProjectNeed) => {
    if (confirm(`Fjern "${need.text}"?`)) void run(() => change.mutateAsync({ kind: 'delete', needId: need.id }))
  }

  return (
    <Card title={open.length > 0 ? `Til næste gang · ${open.length} mangler` : 'Til næste gang'}>
      <form className="stack" style={{ gap: 8 }} onSubmit={add}>
        <div className="row" style={{ gap: 8, alignItems: 'stretch' }}>
          <input
            style={{ flex: 1, minWidth: 0 }}
            value={text}
            maxLength={300}
            placeholder="Hvad mangler vi til næste gang? F.eks. 3 stk. 16A automatsikring"
            onChange={(event) => setText(event.target.value)}
          />
          <button className="btn btn--primary" type="submit" disabled={!text.trim() || change.isPending}>
            Tilføj
          </button>
        </div>
        <div className="row small" style={{ gap: 16, flexWrap: 'wrap' }}>
          <label className="row" style={{ gap: 6, whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={needsOrder}
              onChange={(event) => setNeedsOrder(event.target.checked)}
            />
            Skal bestilles
          </label>
          <label className="row" style={{ gap: 6, whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={needsPacking}
              onChange={(event) => setNeedsPacking(event.target.checked)}
            />
            Skal med i sagskassen
          </label>
        </div>
      </form>

      {actionError && <ErrorBox error={new Error(actionError)} />}

      {open.length === 0 && done.length === 0 && (
        <p className="small muted" style={{ marginTop: 12 }}>
          Intet mangler. Skriv det her, når I opdager noget, der skal med næste gang.
        </p>
      )}
      {open.length === 0 && done.length > 0 && (
        <p className="small muted" style={{ marginTop: 12 }}>
          Alt er klaret.
        </p>
      )}

      <div className="need-list">
        {open.map((need) => (
          <NeedRow key={need.id} need={need} busy={change.isPending} onStep={setStep} onRemove={remove} />
        ))}
      </div>

      {done.length > 0 && (
        <>
          <button className="btn btn--sm" style={{ marginTop: 10 }} onClick={() => setShowDone(!showDone)}>
            {showDone ? 'Skjul klarede' : `Vis klarede (${done.length})`}
          </button>
          {showDone && (
            <div className="need-list">
              {done.map((need) => (
                <NeedRow key={need.id} need={need} busy={change.isPending} onStep={setStep} onRemove={remove} />
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  )
}

function NeedRow({
  need,
  busy,
  onStep,
  onRemove
}: {
  need: ProjectNeed
  busy: boolean
  onStep: (need: ProjectNeed, step: ProjectNeedStep, value: boolean) => void
  onRemove: (need: ProjectNeed) => void
}) {
  const plain = !need.needsOrder && !need.needsPacking

  return (
    <div className={`need-row ${need.isDone ? 'need-row--done' : ''}`}>
      <Avatar user={need.createdBy} size={32} />
      <div className="need-row__body">
        <div className="need-row__text">{need.text}</div>
        <div className="tiny muted">
          {need.createdBy?.displayName ?? 'Ukendt'} · {formatDateTime(need.createdAt)}
        </div>
        <div className="need-row__steps">
          {need.needsOrder && (
            <StepButton
              label="Bestilt"
              at={need.orderedAt}
              by={need.orderedBy}
              busy={busy}
              onChange={(value) => onStep(need, 'ordered', value)}
            />
          )}
          {need.needsPacking && (
            <StepButton
              label="Pakket i sagskassen"
              at={need.packedAt}
              by={need.packedBy}
              busy={busy}
              onChange={(value) => onStep(need, 'packed', value)}
            />
          )}
          {plain && (
            <StepButton
              label="Klaret"
              at={need.doneAt}
              by={need.doneBy}
              busy={busy}
              onChange={(value) => onStep(need, 'done', value)}
            />
          )}
          {need.canDelete && (
            <button className="btn btn--sm need-row__remove" disabled={busy} onClick={() => onRemove(need)}>
              Fjern
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Ét trin. Ikke gjort: en knap. Gjort: et grønt mærke med hvem og hvornår - og et tryk
 * på det fortryder, hvis nogen kom til at trykke.
 */
function StepButton({
  label,
  at,
  by,
  busy,
  onChange
}: {
  label: string
  at?: string | null
  by?: UserRef | null
  busy: boolean
  onChange: (value: boolean) => void
}) {
  if (!at) {
    return (
      <button className="btn btn--sm need-step" disabled={busy} onClick={() => onChange(true)}>
        {label}
      </button>
    )
  }

  return (
    <button
      className="btn btn--sm need-step need-step--on"
      disabled={busy}
      title="Tryk for at fortryde"
      onClick={() => {
        if (confirm(`Fortryd "${label}"?`)) onChange(false)
      }}
    >
      <Avatar user={by} size={18} />
      <span>
        ✓ {label}
        <span className="need-step__who">
          {' '}
          · {by?.displayName ?? 'Ukendt'}, {formatDateTime(at)}
        </span>
      </span>
    </button>
  )
}
